// service/crm/hooks/useContactImport.ts

import { useQuery } from "@tanstack/react-query";
import {
  contactImportApi,
  contactImportQueryKeys,
} from "service/crm/api/contactImport";
import {
  CONTACT_IMPORT_PAGE_SIZE,
  isContactImportFinished,
} from "types/crm/contactImport";

/**
 * The import as it stands, which is what the Dropdowns step reads its Owner,
 * Company and enum values from.
 *
 * Every call that changes the import resets it to DRAFT, so this is never
 * cached beyond the screen that asked for it.
 */
export const useContactImport = (
  importUuid?: string,
  enabled = true,
  /**
   * Set once the import is running. Until then the import only changes when
   * this app changes it, so polling would be noise.
   */
  watchUntilFinished = false,
  /**
   * How much of each of the import's lists to ask for. Growing it is what
   * Show more does, and it is part of the key so a bigger ask is a fresh read
   * rather than the last answer served from cache.
   */
  size: number = CONTACT_IMPORT_PAGE_SIZE
) =>
  useQuery({
    queryFn: () => contactImportApi.getImport(importUuid as string, { page: 0, size }),
    queryKey: contactImportQueryKeys.detailPage(importUuid ?? "", size),
    enabled: Boolean(importUuid) && enabled,
    staleTime: 0,
    // Asking for a longer page is a new key, and so a query with nothing
    // cached. Without this the import reads as absent while it loads, and the
    // screens fall back to the shorter page they already had — then have no
    // reason to rebuild when the longer one lands, since the totals never
    // moved. Holding the previous page keeps that from happening.
    //
    // Only ever this import's own previous page, though. A new file is a new
    // uuid and so a new key too, and holding the last import's answer across
    // that gap hands the screens a different file's Owner and Company to build
    // from — briefly, until the read lands, but long enough for the cards to be
    // built from it and for the answers on them to be carried forward.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[2] === (importUuid ?? "") ? previousData : undefined,
    // A started import finishes in the background. Anything short of a settled
    // status is still in flight — including a pre-Start status left in the
    // cache — so the read keeps going until one of them comes back.
    refetchInterval: (query) => {
      if (!watchUntilFinished) return false;
      return isContactImportFinished(query.state.data?.status) ? false : 1500;
    },
  });
